(function() {
	class ExpandButtonFactory {
		static instances = [];

		static create(selector, customHiddenClass) {
			this.instances = [
				...this.instances,
				...Array.from(document.querySelectorAll(selector)).map((el) => {
					if (!(el instanceof HTMLElement)) {
						throw new TypeError('No HTML element found.');
					}

					return new ExpandButton(el, customHiddenClass);
				})
			];
		}

		static addWindowListener() {
			window.addEventListener('keydown', (e) => {
				if (e.key === 'Escape') {
					const header = document.getElementById('header'),
						isMobileMenuOpen = header && header.classList.contains('open'),
						expandedButtons = ExpandButtonFactory.instances.filter(button => button.isAriaExpanded);

					if (expandedButtons.length === 0) {
						return;
					}

					if (isMobileMenuOpen) {
						const activeElement = document.activeElement;

						// Find the expanded button that contains the active element in its content
						const activeSubmenuButton = expandedButtons.find(button => !button.el.classList.contains('mobile-menu-button') && button._ariaControlsElement && button._ariaControlsElement.contains(activeElement));

						if (activeSubmenuButton) {
							activeSubmenuButton.collapse();
							activeSubmenuButton.el.focus();

							return;
						}

						// Check if any submenu is open
						const openSubmenus = expandedButtons.filter(button => !button.el.classList.contains('mobile-menu-button'));

						if (openSubmenus.length > 0) {
							// If submenus are open, just collapse them
							ExpandButtonFactory.collapseSubmenus();
						} else {
							// If no submenus are open, close the mobile menu
							ExpandButtonFactory.collapseAll();
						}
					} else {
						ExpandButtonFactory.collapseAll();

						// Focus the first button that was expanded
						if (expandedButtons[0] && expandedButtons[0].el) {
							expandedButtons[0].el.focus();
						}
					}
				}
			});

			// Add click outside handler
			document.addEventListener('click', (e) => {
				const header = document.getElementById('header'),
					isMobileMenuOpen = header && header.classList.contains('open'),
					expandedButtons = ExpandButtonFactory.instances.filter(button => button.isAriaExpanded);

				const isExpandButtonInMobileMenu = isMobileMenuOpen && e.target.closest('.expand-button') && e.target.closest('#main-nav');

				if (expandedButtons.length === 0 || isExpandButtonInMobileMenu) {
					return;
				}

				const mobileMenuButton = ExpandButtonFactory.instances.find(
					button => button.el.classList.contains('mobile-menu-button')
				);

				const clickedOutside = expandedButtons.every(button => {
					if (isMobileMenuOpen && button === mobileMenuButton) {
						return false;
					}

					if (button.el.contains(e.target)) {
						// Don't close: clicked on the button itself
						return false;
					}

					if (button._ariaControlsElement && button._ariaControlsElement.contains(e.target)) {
						// Don't close: clicked inside the expanded content
						return false;
					}

					return true;
				});

				if (clickedOutside) {
					if (isMobileMenuOpen) {
						// In mobile mode, don't close the mobile menu, only collapse submenus
						ExpandButtonFactory.collapseSubmenus();
					} else {
						// In desktop mode, collapse everything
						ExpandButtonFactory.collapseAll();
					}
				}
			});
		}

		static collapseAll(exceptButton = null) {
			ExpandButtonFactory.instances.forEach((expandButton) => {
				// Skip the button we want to keep open (if provided)
				if (exceptButton !== expandButton) {
					expandButton.collapse();
				}
			});
		}

		static collapseSubmenus(exceptButton = null) {
			ExpandButtonFactory.instances.forEach((expandButton) => {
				// Skip the mobile menu button and the specified exception
				if (!expandButton.el.classList.contains('mobile-menu-button') && exceptButton !== expandButton) {
					expandButton.collapse();
				}
			});
		}
	}

	class ExpandButton {
		constructor(el, customHiddenClass) {
			this.el = el;
			this._isAriaExpanded = false;
			this._firstActionElement = null;
			this._hiddenClass = 'hidden';
			this._focusAttempts = 0;
			this._keyboardActivated = false;
			this._isMobileMenuButton = this.el.classList.contains('mobile-menu-button');
			this._header = document.getElementById('header');
			this._mouseoutTimer = null;
			this._mouseoutDelay = 300;
			this._isMouseOverButton = false;
			this._isMouseOverContent = false;
			this.isAriaExpanded = this.el.getAttribute('aria-expanded') === 'true';
			const ariaControlsAttr = this.el.getAttribute('aria-controls');
			this._ariaControlsElement = ariaControlsAttr ? document.getElementById(ariaControlsAttr) : null;

			if (!(this._ariaControlsElement instanceof HTMLElement)) {
				throw new TypeError('No referenced element found.');
			}

			this._firstActionElement = this._ariaControlsElement.querySelector(
				'a[href]:not([disabled]), button:not([disabled])'
			);

			if (!(this._firstActionElement instanceof HTMLElement)) {
				throw new TypeError('No first actionable element found.');
			}

			if (customHiddenClass) {
				this._hiddenClass = customHiddenClass;
			}

			if (this._isMobileMenuButton && this._header && this.isAriaExpanded) {
				this._header.classList.add('open');
			}

			this.initListeners();
		}

		get isAriaExpanded() {
			return this._isAriaExpanded;
		}

		set isAriaExpanded(value) {
			this._isAriaExpanded = value;
			this.el.setAttribute('aria-expanded', this.isAriaExpanded.toString());

			if (this._isMobileMenuButton && this._header) {
				if (this.isAriaExpanded) {
					this._header.classList.add('open');
				} else {
					this._header.classList.remove('open');
				}
			}

			if (this.isAriaExpanded) {
				this._ariaControlsElement?.classList.remove(this._hiddenClass);

				if (this._keyboardActivated) {
					this._focusAttempts = 0;
					this._attemptFocus();
				}
			} else {
				this._ariaControlsElement?.classList.add(this._hiddenClass);
			}

			// Reset the keyboard activation flag after handling
			this._keyboardActivated = false;
		}

		_attemptFocus() {
			const maxAttempts = 5;
			const baseDelay = 25;

			if (this._focusAttempts >= maxAttempts) {
				return;
			}

			const delay = baseDelay * Math.pow(1.5, this._focusAttempts);

			setTimeout(() => {
				if (this._firstActionElement && this.isAriaExpanded) {
					try {
						this._firstActionElement.focus();

						if (document.activeElement !== this._firstActionElement) {
							throw new Error('Focus did not take effect');
						}
					} catch (e) {
						this._focusAttempts++;
						this._attemptFocus();
					}
				}
			}, delay);
		}

		initListeners() {
			this.clickHandler();
			this.collapseOnBlurHandler();
			this.mouseHandlers();
		}

		collapse() {
			this.isAriaExpanded = false;
		}

		expand(viaKeyboard = false) {
			this._keyboardActivated = viaKeyboard;

			const isMobileMenuOpen = this._header && this._header.classList.contains('open');

			if (isMobileMenuOpen && !this._isMobileMenuButton) {
				// In mobile mode with submenu buttons, just expand without closing others
				this.isAriaExpanded = true;
			} else {
				// First close all others
				ExpandButtonFactory.collapseAll(this);
				this.isAriaExpanded = true;
			}
		}

		toggle(viaKeyboard = false) {
			this._keyboardActivated = viaKeyboard;

			const isMobileMenuOpen = this._header && this._header.classList.contains('open');

			if (!this.isAriaExpanded) {
				if (isMobileMenuOpen && !this._isMobileMenuButton) {
					// In mobile mode with submenu buttons, just expand without closing others
					this.isAriaExpanded = true;
				} else {
					// If we're expanding, first close all others
					ExpandButtonFactory.collapseAll(this);
					this.isAriaExpanded = true;
				}
			} else {
				// Just collapse this one
				this.isAriaExpanded = false;
			}
		}

		clickHandler() {
			this.el.addEventListener('mousedown', (e) => {
				e.preventDefault();
				this.toggle(false);
			});

			this.el.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === 'Space' || e.key === ' ') {
					e.preventDefault();
					this.toggle(true);
				}
			});
		}

		mouseHandlers() {
			// Skip adding mouseout handlers for the mobile menu button
			if (this._isMobileMenuButton) {
				return;
			}

			// Handle mouse events on the button
			this.el.addEventListener('mouseenter', () => {
				this._isMouseOverButton = true;
				this._clearMouseoutTimer();
			});

			this.el.addEventListener('mouseleave', () => {
				this._isMouseOverButton = false;
				this._checkShouldCloseMenu();
			});

			// Handle mouse events on the dropdown content
			this._ariaControlsElement.addEventListener('mouseenter', () => {
				this._isMouseOverContent = true;
				this._clearMouseoutTimer();
			});

			this._ariaControlsElement.addEventListener('mouseleave', () => {
				this._isMouseOverContent = false;
				this._checkShouldCloseMenu();
			});
		}

		_clearMouseoutTimer() {
			if (this._mouseoutTimer) {
				clearTimeout(this._mouseoutTimer);
				this._mouseoutTimer = null;
			}
		}

		_checkShouldCloseMenu() {
			const isMobileMenuOpen = this._header && this._header.classList.contains('open');

			if (isMobileMenuOpen) {
				return;
			}

			if (!this._isMouseOverButton && !this._isMouseOverContent) {
				this._clearMouseoutTimer();

				this._mouseoutTimer = setTimeout(() => {
					if (this.isAriaExpanded) {
						this.collapse();
					}
				}, this._mouseoutDelay);
			}
		}

		collapseOnBlurHandler() {
			if (this._isMobileMenuButton) {
				return;
			}

			this._ariaControlsElement.addEventListener(
				'focusout',
				(e) => {
					const currentTarget = e.currentTarget,
						isMobileMenuOpen = this._header && this._header.classList.contains('open');

					if (isMobileMenuOpen) {
						return;
					}

					requestAnimationFrame(() => {
						if (!currentTarget.contains(document.activeElement)) {
							this.collapse();
						}
					});
				}
			);
		}
	}

	ExpandButtonFactory.create(
		'.mobile-menu-button',
		'hidden-mobile'
	);

	ExpandButtonFactory.create('.expand-button');

	ExpandButtonFactory.addWindowListener();
})();
